if (!window.emailSignup?.inSignupFrame && !customElements.get('email-popup')) {
  customElements.define(
    'email-popup',
    class EmailPopup extends HTMLElement {
      static DISMISS_KEY = 'ourmoney:email-popup-dismissed';
      static DAY_MS = 24 * 60 * 60 * 1000;
      static MAX_LOAD_WAIT_MS = 4000;

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.signup = this.querySelector('email-signup');
        this.input = this.querySelector('input[type="email"]');
        this.formView = this.querySelector('[data-popup-view="form"]');
        this.successView = this.querySelector('[data-popup-view="success"]');
        this.delayMs = Number(this.dataset.delaySeconds) * 1000;
        this.reshowMs = Number(this.dataset.reshowDays) * EmailPopup.DAY_MS;

        this.onClick = this.onClick.bind(this);
        this.onCancel = (event) => {
          event.preventDefault();
          this.dismiss('escape');
        };
        this.onClose = () => document.documentElement.classList.remove('email-popup-open');
        this.onSignupResult = (event) => {
          if (event.detail.outcome === 'success') this.showSuccess();
        };
        this.addEventListener('email-signup:result', this.onSignupResult);
        this.dialog.addEventListener('click', this.onClick);
        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('close', this.onClose);

        if (window.Shopify?.designMode) {
          this.bindThemeEditor();
        } else {
          this.arrive();
        }
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        clearTimeout(this.loadCap);
        document.removeEventListener('visibilitychange', this.onVisible);
        document.removeEventListener('shopify:section:select', this.onEditorSelect);
        document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
        document.documentElement.classList.remove('email-popup-open');
      }

      arrive() {
        // Only after a full-page fallback submit: Shopify reloads at #this-form. Keep the visitor on the front
        // page (no jump to the form's position) and reopen the pop-up on the result.
        const outcome = this.signup?.outcome;
        if (this.signup && location.hash === `#${this.signup.form.id}`) this.returnToTop();
        if (outcome) return outcome === 'success' ? this.open('success') : this.open('form', { focusInput: true });

        const params = new URLSearchParams(location.search);
        // ?popup=show previews it again for people who already closed it or subscribed (e.g. the store owner).
        if (params.get('popup') === 'show') return this.schedule();
        if (window.emailSignup.isSubscribed() || this.recentlyDismissed()) return;
        if (params.has('customer_posted')) return;
        this.schedule();
      }

      returnToTop() {
        const url = new URL(location.href);
        url.searchParams.delete('customer_posted');
        url.hash = '';
        history.replaceState(null, '', url);
        window.scrollTo(0, 0);
        window.addEventListener('load', () => window.scrollTo(0, 0), { once: true });
      }

      showSuccess() {
        if (!this.dialog.open) return this.open('success');
        this.formView.hidden = true;
        this.successView.hidden = false;
        this.successView.querySelector('[data-popup-focus]').focus();
      }

      recentlyDismissed() {
        const dismissed = window.emailSignup.read(EmailPopup.DISMISS_KEY);
        return Boolean(dismissed && Date.now() - dismissed.at < this.reshowMs);
      }

      // The countdown starts once the page has rendered (load, capped for slow networks) and only runs while
      // the tab is visible, so visitors see the landing page first and the pop-up never appears unseen.
      schedule() {
        const start = () => {
          this.timer = setTimeout(() => this.openWhenIdle(), this.delayMs);
        };
        const startWhenVisible = () => {
          if (document.visibilityState === 'visible') return start();
          this.onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            document.removeEventListener('visibilitychange', this.onVisible);
            start();
          };
          document.addEventListener('visibilitychange', this.onVisible);
        };
        if (document.readyState === 'complete') return startWhenVisible();

        let started = false;
        const onReady = () => {
          if (started) return;
          started = true;
          clearTimeout(this.loadCap);
          window.removeEventListener('load', onReady);
          startWhenVisible();
        };
        window.addEventListener('load', onReady);
        this.loadCap = setTimeout(onReady, EmailPopup.MAX_LOAD_WAIT_MS);
      }

      // Don't interrupt someone who is typing or already in a menu, drawer or other modal.
      openWhenIdle() {
        const busy =
          document.activeElement?.matches('input, textarea, select') ||
          document.querySelector(
            'dialog[open], details[open] > .menu-drawer, details[open] > .search-modal, .drawer.active, modal-dialog[open]'
          );
        if (busy) {
          this.timer = setTimeout(() => this.openWhenIdle(), 3000);
          return;
        }
        this.open('form');
      }

      open(view, { focusInput = false } = {}) {
        if (this.dialog.open) return;
        this.formView.hidden = view !== 'form';
        this.successView.hidden = view !== 'success';
        document.documentElement.classList.add('email-popup-open');
        this.dialog.showModal();
        if (view === 'success') this.successView.querySelector('[data-popup-focus]').focus();
        if (focusInput) this.input.focus();
        window.emailSignup.publish('email_popup_displayed', { view });
      }

      onClick(event) {
        const closer = event.target.closest('[data-popup-close]');
        if (closer) {
          this.dismiss(closer.dataset.popupClose);
        } else if (event.target === this.dialog) {
          // The dialog has no padding, so a click that lands on the dialog itself is on the backdrop.
          this.dismiss('backdrop');
        }
      }

      dismiss(reason) {
        if (!this.dialog.open) return;
        const subscribed = !this.successView.hidden;
        if (!subscribed && !window.Shopify?.designMode) {
          window.emailSignup.write(EmailPopup.DISMISS_KEY, { at: Date.now() });
        }
        window.emailSignup.publish('email_popup_dismissed', { reason, subscribed });
        this.close();
      }

      close() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.dialog.close();
          return;
        }
        const finish = () => {
          clearTimeout(this.closeFallback);
          this.dialog.classList.remove('is-closing');
          if (this.dialog.open) this.dialog.close();
        };
        this.dialog.classList.add('is-closing');
        this.dialog.addEventListener('animationend', finish, { once: true });
        this.closeFallback = setTimeout(finish, 400);
      }

      bindThemeEditor() {
        this.onEditorSelect = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open('form');
        };
        this.onEditorDeselect = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId && this.dialog.open) this.dialog.close();
        };
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
      }
    }
  );
}
